import { forwardRef, useCallback, useState } from "react";
import { clsx } from "clsx";

import { Icon } from "../icon/Icon";

import "./TextInput.css";

import type {
  CSSProperties,
  ChangeEvent as ReactChangeEvent,
  SyntheticEvent,
} from "react";

export type TextInputType = "text" | "search";

export interface TextInputChangeEvent {
  value: string;
}

export interface TextInputProps {
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  type?: TextInputType;
  style?: CSSProperties;
  value?: string;
  onChange?: (event: TextInputChangeEvent) => void;
  onClick?: (event: SyntheticEvent<HTMLInputElement>) => void;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInputImpl(
    {
      disabled,
      name,
      placeholder,
      readOnly,
      type = "text",
      style,
      value,
      onChange,
      onClick,
    },
    ref,
  ) {
    const [isFocused, setIsFocused] = useState(false);

    const isSearch = type === "search";

    const handleFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    const handleClear = useCallback(() => {
      onChange?.({ value: "" });
    }, [onChange]);

    const handleChange = useCallback(
      (e: ReactChangeEvent<HTMLInputElement>) => {
        onChange?.({ value: e.target.value });
      },
      [onChange],
    );

    return (
      <div
        className={clsx("TextInput", {
          "TextInput--disabled": disabled,
          "TextInput--focused": isFocused,
        })}
        style={style}
      >
        {isSearch && (
          <div className="TextInput__control">
            <Icon icon="magnifying-glass" />
          </div>
        )}

        <input
          ref={ref}
          type={type}
          value={value}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={onClick}
          onFocus={handleFocus}
        />

        {isSearch && value && (
          <button
            className="TextInput__control"
            onClick={handleClear}
            tabIndex={-1}
          >
            <Icon icon="circle-xmark" variant="solid" />
          </button>
        )}
      </div>
    );
  },
);
